import React, { useState, useEffect, useCallback } from 'react';
import Select from 'react-select';
import moment from 'moment';
import fetchAPI from '../../common/fetchAPI';
import { useAppSelector, useAppDispatch } from '../../app/hooks';
import { getGlobalAccountPreferenceDataAsync } from '../../common/GlobalSlice';
import { getTimer } from '../timesheet/timesheetSlice';
import { ShowToast } from '../../common/Toastr';

declare const chrome: any;

interface ExtensionTimerProps {
  onSwitchToManual: () => void;
}

const formatOptionWithIcon = (option: any, iconClass: string, context: 'menu' | 'value') => {
  if (context === 'value') {
    return (
      <div className="d-flex align-items-center">
        <i className={`bi ${iconClass} me-3 text-gray-700 fs-7`}></i>
        <span className="extensionHeading" style={{  fontWeight: 'bold' }}>{option.label}</span>
      </div>
    );
  }
  return option.label;
};



const timerDropdownStyles = {
  control: (base: any, state: any) => ({
    ...base,
    minHeight: '38px',
    height: '38px',
    borderRadius: '8px',
    borderColor: state.isFocused ? '#1B64F2' : '#dddddd',
    boxShadow: 'none',
    backgroundColor: state.isDisabled ? '#F9FAFB' : '#FFFFFF',
    '&:hover': { borderColor: '#1B64F2' },
  }),
  valueContainer: (base: any) => ({ ...base, padding: '0 8px', height: '38px', display: 'flex', alignItems: 'center' }),
  input: (base: any) => ({ ...base, margin: 0, padding: 0, fontSize: '13px' }),
  singleValue: (base: any) => ({ ...base, fontSize: '13px', color: '#181C32', margin: 0, display: 'flex', alignItems: 'center' }),
  placeholder: (base: any) => ({ ...base, fontSize: '13px', color: '#A1A5B7' }),
  option: (base: any, state: any) => ({
    ...base,
    backgroundColor: state.isSelected ? '#1B64F2' : state.isFocused ? '#F4F6F9' : '#FFFFFF',
    color: state.isSelected ? '#FFFFFF' : '#181C32',
    fontSize: '13px',
    cursor: 'pointer',
    padding: '8px 12px',
    fontWeight: state.isSelected ? '500' : '400',
  }),
  menu: (base: any) => ({ ...base, borderRadius: '8px', boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.08)', zIndex: 9999, overflow: 'hidden' }),
  menuList: (base: any) => ({ ...base, padding: 0 }),
};

export const ExtensionTimer: React.FC<ExtensionTimerProps> = ({ onSwitchToManual }) => {
  const dispatch = useAppDispatch();

  // Redux Global States
  const globalState = useAppSelector((state) => state.globalState);
  const { 
    sessionAccountEmployeeId, 
    timeZoneId, 
    showClientInTimesheet, 
    showCostCenterInTimeSheet, 
    showWorkTypeInTimeSheet 
  } = globalState || {};
  
  const { defaultWorkTypeId } = useAppSelector((state) => state.timesheet || {});

  const [activeEmpId, setActiveEmpId] = useState<any | string | null>(null);
  const [isClientVisible, setIsClientVisible] = useState<any>(Boolean(showClientInTimesheet));
  const [isCostCenterVisible, setIsCostCenterVisible] = useState<boolean>(Boolean(showCostCenterInTimeSheet));
  const [isWorkTypeVisible, setIsWorkTypeVisible] = useState<boolean>(Boolean(showWorkTypeInTimeSheet));
  const [isStorageLoaded, setIsStorageLoaded] = useState<boolean>(false);
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);


  const [savedClientId, setSavedClientId] = useState<any>(null);
  const [savedProjectId, setSavedProjectId] = useState<any>(null);
  const [savedTaskId, setSavedTaskId] = useState<any>(null);
  const [savedCostCenterId, setSavedCostCenterId] = useState<any>(null);
  const [savedWorkTypeId, setSavedWorkTypeId] = useState<any>(null);


  const [clientOptions, setClientOptions] = useState<{ value: any; label: string }[]>([]);
  const [projectOptions, setProjectOptions] = useState<{ value: any; label: string }[]>([]);
  const [taskOptions, setTaskOptions] = useState<{ value: any; label: string }[]>([]);
  const [costCenterOptions, setCostCenterOptions] = useState<{ value: any; label: string }[]>([]);
  const [workTypeOptions, setWorkTypeOptions] = useState<{ value: any; label: string }[]>([]);


  const [selectedClient, setSelectedClient] = useState<any>(null);
  const [selectedProject, setSelectedProject] = useState<any>(null);
  const [selectedTask, setSelectedTask] = useState<any>(null);
  const [selectedCostCenter, setSelectedCostCenter] = useState<any>(null);
  const [selectedWorkType, setSelectedWorkType] = useState<any>(null);

  const [activeTimerTaskId, setActiveTimerTaskId] = useState<any>(null);
  const [activeTimerCostCenterId, setActiveTimerCostCenterId] = useState<any>(null);

  const [loadingClients, setLoadingClients] = useState(false);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [loadingCostCenters, setLoadingCostCenters] = useState(false);
  const [loadingWorkTypes, setLoadingWorkTypes] = useState(false);

  
  const [timerState, setTimerState] = useState<'idle' | 'running' | 'stopped'>('idle');
  const [seconds, setSeconds] = useState(0);
  const [description, setDescription] = useState('');
  const [lastEntryTimerId, setLastEntryTimerId] = useState<any>(null);
  const [accountEmployeeTimeEntryPeriodId, setAccountEmployeeTimeEntryPeriodId] = useState<any>(null);
  const [periodDates, setPeriodDates] = useState({ startDate: '', endDate: '', viewType: 'Weekly' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const persistStorage = useCallback((dataToSave: Record<string, any>) => {
    try {
      if (typeof chrome !== 'undefined' && chrome?.storage?.local) {
        chrome.storage.local.set(dataToSave);
      } else {
        Object.entries(dataToSave).forEach(([k, v]) => {
          if (v === null || v === undefined) localStorage.removeItem(k);
          else localStorage.setItem(k, JSON.stringify(v));
        });
      }
    } catch (e) {
      console.error('Storage write error:', e);
    }
  }, []);

 useEffect(() => {
    if (typeof chrome !== 'undefined' && chrome?.storage?.onChanged) {
      const handleStorageChange = (changes: any, areaName: string) => {
        if (areaName === 'local') {
          if (changes.showClientInTimesheet !== undefined) {
            const val = Boolean(changes.showClientInTimesheet.newValue);
            setIsClientVisible(val);
            if (!val) setSelectedClient(null);
          }
          if (changes.showCostCenterInTimeSheet !== undefined || changes.showCostCenterInTimesheet !== undefined) {
            const val = Boolean(changes.showCostCenterInTimeSheet?.newValue ?? changes.showCostCenterInTimesheet?.newValue);
            setIsCostCenterVisible(val);
            if (!val) setSelectedCostCenter(null);
          }
          if (changes.showWorkTypeInTimeSheet !== undefined || changes.showWorkTypeInTimesheet !== undefined) {
            const val = Boolean(changes.showWorkTypeInTimeSheet?.newValue ?? changes.showWorkTypeInTimeSheet?.newValue);
            setIsWorkTypeVisible(val);
            if (!val) setSelectedWorkType(null);
          }
        }
      };
      chrome.storage.onChanged.addListener(handleStorageChange);
      return () => chrome.storage.onChanged.removeListener(handleStorageChange);
    }
  }, []);

  
  useEffect(() => {
    if (showClientInTimesheet !== undefined) setIsClientVisible(Boolean(showClientInTimesheet));
  }, [showClientInTimesheet]);

  useEffect(() => {
    if (showCostCenterInTimeSheet !== undefined) setIsCostCenterVisible(Boolean(showCostCenterInTimeSheet));
  }, [showCostCenterInTimeSheet]);

  useEffect(() => {
    if (showWorkTypeInTimeSheet !== undefined) setIsWorkTypeVisible(Boolean(showWorkTypeInTimeSheet));
  }, [showWorkTypeInTimeSheet]);


  const parseBoolValue = (data: any, keys: string[]): boolean | undefined => {
    if (!data) return undefined;
    for (const key of keys) {
      if (data[key] !== undefined && data[key] !== null) {
        const val = data[key];
        return val === true || val === 1 || val === "true" || val === "1";
      }
    }
    return undefined;
  };

  // const applyFreshPreferences = useCallback(async (prefData: any, employeeId: number | string | null) => {
  //   const rawData = prefData?.data || prefData || {};

  
  //   const clientVal = parseBoolValue(rawData, ['ShowClientInTimesheet', 'showClientInTimesheet', 'ShowClientInTimeSheet', 'showClientInTimeSheet']);
  //   const costCenterVal = parseBoolValue(rawData, ['ShowCostCenterInTimeSheet', 'showCostCenterInTimeSheet', 'ShowCostCenterInTimesheet', 'showCostCenterInTimesheet']);
  //   const workTypeVal = parseBoolValue(rawData, ['ShowWorkTypeInTimeSheet', 'showWorkTypeInTimeSheet', 'ShowWorkTypeInTimesheet', 'showWorkTypeInTimesheet']);

  //   const clientFlag = clientVal ?? Boolean(showClientInTimesheet ?? isClientVisible);
  //   const costCenterFlag = costCenterVal ?? Boolean(showCostCenterInTimeSheet ?? isCostCenterVisible);
  //   const workTypeFlag = workTypeVal ?? Boolean(showWorkTypeInTimeSheet ?? isWorkTypeVisible);

  //   setIsClientVisible(clientFlag);
  //   setIsCostCenterVisible(costCenterFlag);
  //   setIsWorkTypeVisible(workTypeFlag);

  //   persistStorage({
  //     showClientInTimesheet: clientFlag,
  //     showCostCenterInTimeSheet: costCenterFlag,
  //     showWorkTypeInTimeSheet: workTypeFlag,
  //     timeZoneId: rawData?.TimeZoneId || rawData?.timeZoneId || timeZoneId
  //   });

  //   const empId = employeeId || activeEmpId;
  //   if (empId) {
  //     if (clientFlag) {
  //       setLoadingClients(true);
  //       try {
  //         const clientRes = await fetchAPI(`api/Timesheets/GetAccountClientForTimesheet/${empId}/0`, 'GET', '', true);
  //         const clientData = await clientRes.json();
  //         const list = Array.isArray(clientData) ? clientData : clientData?.data || [];
  //         setClientOptions(list.map((item: any) => ({ value: item.AccountClientId, label: item.ClientName })));
  //       } catch (err) { console.error('Error fetching clients:', err); } 
  //       finally { setLoadingClients(false); }
  //     } else {
  //       setSelectedClient(null);
  //       setClientOptions([]);
  //       setLoadingProjects(true);
  //       try {
  //         const projRes = await fetchAPI(`api/Projects/GetAccountProjectsForTimesheet/0/${empId}`, 'GET', '', true);
  //         const projData = await projRes.json();
  //         const list = Array.isArray(projData) ? projData : projData?.data || [];
  //         setProjectOptions(list.map((item: any) => ({ value: item.AccountProjectId, label: item.ProjectName })));
  //       } catch (err) { console.error('Error fetching projects:', err); } 
  //       finally { setLoadingProjects(false); }
  //     }

  //     if (workTypeFlag) {
  //       setLoadingWorkTypes(true);
  //       try {
  //         const wtRes = await fetchAPI('api/WorkTypes', 'GET', '', true);
  //         const wtData = await wtRes.json();
  //         const list = Array.isArray(wtData) ? wtData : wtData?.data || [];
  //         setWorkTypeOptions(list.map((item: any) => ({ value: item.AccountWorkTypeId, label: item.AccountWorkType })));
  //       } catch (err) { console.error('Error fetching work types:', err); }
  //       finally { setLoadingWorkTypes(false); }
  //     }
  //   }
  // }, [showClientInTimesheet, showCostCenterInTimeSheet, showWorkTypeInTimeSheet, isClientVisible, isCostCenterVisible, isWorkTypeVisible, activeEmpId, timeZoneId, persistStorage]);
const applyFreshPreferences = useCallback(async (prefData: any, employeeId: number | string | null) => {
    console.log("--- Fresh Preferences API Response ---", prefData);

    // Yahan check kar lein ke data array ka pehla item uthana hai ya object
    const actualData = prefData?.data;
    const rawData = Array.isArray(actualData) ? actualData[0] : (Array.isArray(prefData) ? prefData[0] : prefData) || {};

    console.log("Extracted Raw Data:", rawData);

    const clientVal = parseBoolValue(rawData, ['ShowClientInTimesheet', 'showClientInTimesheet', 'ShowClientInTimeSheet', 'showClientInTimeSheet']);
    const costCenterVal = parseBoolValue(rawData, ['ShowCostCenterInTimeSheet', 'showCostCenterInTimeSheet', 'ShowCostCenterInTimesheet', 'showCostCenterInTimesheet']);
    const workTypeVal = parseBoolValue(rawData, ['ShowWorkTypeInTimeSheet', 'showWorkTypeInTimeSheet', 'ShowWorkTypeInTimesheet', 'showWorkTypeInTimeSheet']);

    const clientFlag = clientVal ?? Boolean(showClientInTimesheet ?? isClientVisible);
    const costCenterFlag = costCenterVal ?? Boolean(showCostCenterInTimeSheet ?? isCostCenterVisible);
    const workTypeFlag = workTypeVal ?? Boolean(showWorkTypeInTimeSheet ?? isWorkTypeVisible);

    setIsClientVisible(clientFlag);
    setIsCostCenterVisible(costCenterFlag);
    setIsWorkTypeVisible(workTypeFlag);

    persistStorage({
      showClientInTimesheet: clientFlag,
      showCostCenterInTimeSheet: costCenterFlag,
      showWorkTypeInTimeSheet: workTypeFlag,
      timeZoneId: rawData?.TimeZoneId || rawData?.timeZoneId || timeZoneId
    });

    const empId = employeeId || activeEmpId;
    if (empId) {
      if (clientFlag) {
        setLoadingClients(true);
        try {
          const clientRes = await fetchAPI(`api/Timesheets/GetAccountClientForTimesheet/${empId}/0`, 'GET', '', true);
          const clientData = await clientRes.json();
          const list = Array.isArray(clientData) ? clientData : clientData?.data || [];
          setClientOptions(list.map((item: any) => ({ value: item.AccountClientId, label: item.ClientName })));
        } catch (err) { console.error('Error fetching clients:', err); } 
        finally { setLoadingClients(false); }
      } else {
        setSelectedClient(null);
        setClientOptions([]);
        setLoadingProjects(true);
        try {
          const projRes = await fetchAPI(`api/Projects/GetAccountProjectsForTimesheet/0/${empId}`, 'GET', '', true);
          const projData = await projRes.json();
          const list = Array.isArray(projData) ? projData : projData?.data || [];
          setProjectOptions(list.map((item: any) => ({ value: item.AccountProjectId, label: item.ProjectName })));
        } catch (err) { console.error('Error fetching projects:', err); } 
        finally { setLoadingProjects(false); }
      }

      if (workTypeFlag) {
        setLoadingWorkTypes(true);
        try {
          const wtRes = await fetchAPI('api/WorkTypes', 'GET', '', true);
          const wtData = await wtRes.json();
          const list = Array.isArray(wtData) ? wtData : wtData?.data || [];
          setWorkTypeOptions(list.map((item: any) => ({ value: item.AccountWorkTypeId, label: item.AccountWorkType })));
        } catch (err) { console.error('Error fetching work types:', err); }
        finally { setLoadingWorkTypes(false); }
      }
    }
  }, [showClientInTimesheet, showCostCenterInTimeSheet, showWorkTypeInTimeSheet, isClientVisible, isCostCenterVisible, isWorkTypeVisible, activeEmpId, timeZoneId, persistStorage]);
 
 
  const handleRefreshAllPreferences = async () => {
    setIsRefreshing(true);
    try {
      const res: any = await dispatch(getGlobalAccountPreferenceDataAsync() as any).unwrap();
      console.log('res :', res)
      await applyFreshPreferences(res, activeEmpId || sessionAccountEmployeeId);
      await dispatch(getTimer() as any);
    } catch (err) { 
      console.error('Error refreshing preferences:', err); 
    } finally { 
      setIsRefreshing(false); 
    }
  };

  useEffect(() => {
    if (typeof chrome !== 'undefined' && chrome?.storage?.onChanged) {
      const handleStorageChange = (changes: any, areaName: string) => {
        if (areaName === 'local') {
          if (changes.showClientInTimesheet !== undefined) {
            const val = Boolean(changes.showClientInTimesheet.newValue);
            setIsClientVisible(val);
            if (!val) setSelectedClient(null);
          }
          if (changes.showCostCenterInTimeSheet !== undefined || changes.showCostCenterInTimesheet !== undefined) {
            const val = Boolean(changes.showCostCenterInTimeSheet?.newValue ?? changes.showCostCenterInTimesheet?.newValue);
            setIsCostCenterVisible(val);
            if (!val) setSelectedCostCenter(null);
          }
          if (changes.showWorkTypeInTimeSheet !== undefined || changes.showWorkTypeInTimesheet !== undefined) {
            const val = Boolean(changes.showWorkTypeInTimeSheet?.newValue ?? changes.showWorkTypeInTimesheet?.newValue);
            setIsWorkTypeVisible(val);
            if (!val) setSelectedWorkType(null);
          }
        }
      };
      chrome.storage.onChanged.addListener(handleStorageChange);
      return () => chrome.storage.onChanged.removeListener(handleStorageChange);
    }
  }, []);

  useEffect(() => {
    const keysToFetch = ['sessionAccountEmployeeId', 'accountEmployeeId', 'showClientInTimesheet', 'showCostCenterInTimeSheet', 'showCostCenterInTimesheet', 'showWorkTypeInTimeSheet', 'showWorkTypeInTimesheet', 'timeZoneId', 'manual_clientId', 'manual_projectId', 'manual_taskId', 'manual_costCenterId', 'manual_workTypeId'];

    if (typeof chrome !== 'undefined' && chrome?.storage?.local) {
      chrome.storage.local.get(keysToFetch, (result: any) => {
        const resolvedEmpId = result?.accountEmployeeId || result?.sessionAccountEmployeeId || sessionAccountEmployeeId;
        if (resolvedEmpId) setActiveEmpId(Number(resolvedEmpId));

        if (result?.showClientInTimesheet !== undefined) setIsClientVisible(Boolean(result.showClientInTimesheet));
        if (result?.showCostCenterInTimeSheet !== undefined || result?.showCostCenterInTimesheet !== undefined) setIsCostCenterVisible(Boolean(result?.showCostCenterInTimeSheet ?? result?.showCostCenterInTimesheet));
        if (result?.showWorkTypeInTimeSheet !== undefined || result?.showWorkTypeInTimesheet !== undefined) setIsWorkTypeVisible(Boolean(result?.showWorkTypeInTimeSheet ?? result?.showWorkTypeInTimesheet));

        setSavedClientId(result?.manual_clientId ?? null);
        setSavedProjectId(result?.manual_projectId ?? null);
        setSavedTaskId(result?.manual_taskId ?? null);
        setSavedCostCenterId(result?.manual_costCenterId ?? null);
        setSavedWorkTypeId(result?.manual_workTypeId ?? null);
        setIsStorageLoaded(true);
      });
    } else {
      if (sessionAccountEmployeeId) setActiveEmpId(Number(sessionAccountEmployeeId));
      const parseLocal = (k: string) => { const val = localStorage.getItem(k); return val ? JSON.parse(val) : null; };
      setSavedClientId(parseLocal('manual_clientId'));
      setSavedProjectId(parseLocal('manual_projectId'));
      setSavedTaskId(parseLocal('manual_taskId'));
      setSavedCostCenterId(parseLocal('manual_costCenterId'));
      setSavedWorkTypeId(parseLocal('manual_workTypeId'));
      setIsStorageLoaded(true);
    }
  }, [sessionAccountEmployeeId]);

  const handleClearAll = () => {
    setSelectedClient(null); setSelectedProject(null); setSelectedTask(null); setSelectedCostCenter(null); setSelectedWorkType(null); setDescription('');
    if (isClientVisible) setProjectOptions([]);
    setTaskOptions([]); setCostCenterOptions([]);
    setSavedClientId(null); setSavedProjectId(null); setSavedTaskId(null); setSavedCostCenterId(null); setSavedWorkTypeId(null);
    persistStorage({ manual_clientId: null, manual_projectId: null, manual_taskId: null, manual_costCenterId: null, manual_workTypeId: null });
  };

  const handleClientChange = (opt: any) => {
    setSelectedClient(opt); const val = opt?.value ?? null;
    setSavedClientId(val); setSavedProjectId(null); setSavedTaskId(null); setSavedCostCenterId(null);
    persistStorage({ manual_clientId: val, manual_projectId: null, manual_taskId: null, manual_costCenterId: null });
    if (!opt) { setSelectedProject(null); setSelectedTask(null); setSelectedCostCenter(null); }
  };

  const handleProjectChange = (opt: any) => {
    setSelectedProject(opt); const val = opt?.value ?? null;
    setSavedProjectId(val); setSavedTaskId(null); setSavedCostCenterId(null);
    persistStorage({ manual_projectId: val, manual_taskId: null, manual_costCenterId: null });
    if (!opt) { setSelectedTask(null); setSelectedCostCenter(null); }
  };

  const handleTaskChange = (opt: any) => {
    setSelectedTask(opt); const val = opt?.value ?? null;
    setSavedTaskId(val); setSavedCostCenterId(null);
    persistStorage({ manual_taskId: val, manual_costCenterId: null });
    if (!opt) setSelectedCostCenter(null);
  };

  const handleCostCenterChange = (opt: any) => {
    setSelectedCostCenter(opt); const val = opt?.value ?? null;
    setSavedCostCenterId(val);
    persistStorage({ manual_costCenterId: val });
  };

  const handleWorkTypeChange = (opt: any) => {
    setSelectedWorkType(opt); const val = opt?.value ?? null;
    setSavedWorkTypeId(val);
    persistStorage({ manual_workTypeId: val });
  };
  // useEffect(() => {
  //   if (!isStorageLoaded || !activeEmpId) return;
  //   const todayDate = moment().format('YYYY-MM-DD');

  //   fetchAPI(`api/Timesheets/GetTimesheetPeriodForReact/${activeEmpId}/${todayDate}`, 'GET', '', true)
  //     .then((res) => res.json())
  //     .then((data) => {
  //       const periodObj = (data?.data || data)?.AccountEmployeeTimeEntryPeriod || {};
  //       if (periodObj?.AccountEmployeeTimeEntryPeriodId) {
  //         setAccountEmployeeTimeEntryPeriodId(periodObj.AccountEmployeeTimeEntryPeriodId);
  //         setPeriodDates({ startDate: periodObj?.TimeEntryStartDate || todayDate, endDate: periodObj?.TimeEntryEndDate || todayDate, viewType: periodObj?.TimeEntryViewType || 'Weekly' });
  //       }
  //     })
  //     .catch((err) => console.error('Error fetching period:', err));

  //   dispatch(getTimer() as any).then((action: any) => {
  //     const timerList = Array.isArray(action?.payload) ? action.payload : (action?.payload?.data || []);
  //     if (timerList && timerList.length > 0) {
  //       const activeTimer = timerList[0];
  //       setLastEntryTimerId(activeTimer.TimeEntryTimerId); 
  //       setSeconds(activeTimer.TotalSeconds || 0); 
  //       setDescription(activeTimer.Description || ''); 
  //       setTimerState('running');
  //       setActiveTimerTaskId(activeTimer.AccountProjectTaskId || null);
  //       setActiveTimerCostCenterId(activeTimer.AccountCostCenterId || null);
  //     }
  //   }).catch((err: any) => console.error("Error fetching timer:", err));
  // }, [isStorageLoaded, activeEmpId, dispatch]);


  useEffect(() => {
    if (!isStorageLoaded || !activeEmpId) return;

    // 1. Extension open hotay hi automatically fresh preferences fetch karein
    handleRefreshAllPreferences();

    const todayDate = moment().format('YYYY-MM-DD');

    fetchAPI(`api/Timesheets/GetTimesheetPeriodForReact/${activeEmpId}/${todayDate}`, 'GET', '', true)
      .then((res) => res.json())
      .then((data) => {
        const periodObj = (data?.data || data)?.AccountEmployeeTimeEntryPeriod || {};
        if (periodObj?.AccountEmployeeTimeEntryPeriodId) {
          setAccountEmployeeTimeEntryPeriodId(periodObj.AccountEmployeeTimeEntryPeriodId);
          setPeriodDates({ startDate: periodObj?.TimeEntryStartDate || todayDate, endDate: periodObj?.TimeEntryEndDate || todayDate, viewType: periodObj?.TimeEntryViewType || 'Weekly' });
        }
      })
      .catch((err) => console.error('Error fetching period:', err));

    dispatch(getTimer() as any).then((action: any) => {
      const timerList = Array.isArray(action?.payload) ? action.payload : (action?.payload?.data || []);
      if (timerList && timerList.length > 0) {
        const activeTimer = timerList[0];
        setLastEntryTimerId(activeTimer.TimeEntryTimerId); 
        setSeconds(activeTimer.TotalSeconds || 0); 
        setDescription(activeTimer.Description || ''); 
        setTimerState('running');
        setActiveTimerTaskId(activeTimer.AccountProjectTaskId || null);
        setActiveTimerCostCenterId(activeTimer.AccountCostCenterId || null);
      }
    }).catch((err: any) => console.error("Error fetching timer:", err));
  }, [isStorageLoaded, activeEmpId, dispatch]);


  useEffect(() => {
    if (!isStorageLoaded || !activeEmpId) return;

    if (isClientVisible) {
      setLoadingClients(true);
      fetchAPI(`api/Timesheets/GetAccountClientForTimesheet/${activeEmpId}/0`, 'GET', '', true)
        .then((res) => res.json())
        .then((data) => {
          const formatted = (Array.isArray(data) ? data : data?.data || []).map((item: any) => ({ value: item.AccountClientId, label: item.ClientName }));
          setClientOptions(formatted);
          const match = formatted.find((c: any) => Number(c.value) === Number(savedClientId));
          setSelectedClient(match || null);
        })
        .catch((err) => console.error('Error fetching clients:', err))
        .finally(() => setLoadingClients(false));
    } else {
      setLoadingProjects(true);
      fetchAPI(`api/Projects/GetAccountProjectsForTimesheet/0/${activeEmpId}`, 'GET', '', true)
        .then((res) => res.json())
        .then((data) => {
          const formatted = (Array.isArray(data) ? data : data?.data || []).map((item: any) => ({ value: item.AccountProjectId, label: item.ProjectName }));
          setProjectOptions(formatted);
          const match = formatted.find((p: any) => Number(p.value) === Number(savedProjectId));
          setSelectedProject(match || null);
        })
        .catch((err) => console.error('Error fetching projects:', err))
        .finally(() => setLoadingProjects(false));
    }

    if (isWorkTypeVisible) {
      setLoadingWorkTypes(true);
      fetchAPI('api/WorkTypes', 'GET', '', true)
        .then((res) => res.json())
        .then((data) => {
          const formatted = (Array.isArray(data) ? data : data?.data || []).map((item: any) => ({ value: item.AccountWorkTypeId, label: item.AccountWorkType }));
          setWorkTypeOptions(formatted);
          const targetId = savedWorkTypeId ?? defaultWorkTypeId;
          const match = formatted.find((w: any) => Number(w.value) === Number(targetId));
          setSelectedWorkType(match || null);
        })
        .catch((err) => console.error('Error fetching work types:', err))
        .finally(() => setLoadingWorkTypes(false));
    }
  }, [isStorageLoaded, activeEmpId, isClientVisible, isWorkTypeVisible, savedClientId, savedProjectId, savedWorkTypeId, defaultWorkTypeId]);

  useEffect(() => {
    if (!isClientVisible || !selectedClient?.value || !activeEmpId) {
      if (isClientVisible && !selectedClient) { setProjectOptions([]); setSelectedProject(null); }
      return;
    }
    setLoadingProjects(true);
    fetchAPI(`api/Projects/GetAccountProjectsForTimesheet/${selectedClient.value}/${activeEmpId}`, 'GET', '', true)
      .then((res) => res.json())
      .then((data) => {
        const formatted = (Array.isArray(data) ? data : data?.data || []).map((item: any) => ({ value: item.AccountProjectId, label: item.ProjectName }));
        setProjectOptions(formatted);
        const match = formatted.find((p: any) => Number(p.value) === Number(savedProjectId));
        setSelectedProject(match || null);
      })
      .catch((err) => console.error('Error fetching projects:', err))
      .finally(() => setLoadingProjects(false));
  }, [selectedClient, activeEmpId, isClientVisible, savedProjectId]);

  useEffect(() => {
    if (!selectedProject?.value || !activeEmpId) { setTaskOptions([]); return; }
    setLoadingTasks(true);
    fetchAPI(`api/Timesheets/GetAccountProjectTasksForTimesheet/${selectedProject.value}/${activeEmpId}`, 'GET', '', true)
      .then((res) => res.json())
      .then((data) => {
        const formatted = (Array.isArray(data) ? data : data?.data || []).map((item: any) => ({ value: item.AccountProjectTaskId, label: item.TaskName }));
        setTaskOptions(formatted);
        const targetId = activeTimerTaskId || savedTaskId;
        const match = formatted.find((t: any) => Number(t.value) === Number(targetId));
        if (match) setSelectedTask(match);
      })
      .catch((err) => console.error('Error fetching tasks:', err))
      .finally(() => setLoadingTasks(false));
  }, [selectedProject?.value, activeEmpId, savedTaskId, activeTimerTaskId]);

  useEffect(() => {
    if (!isCostCenterVisible || !selectedTask?.value || !selectedProject?.value || !activeEmpId) { setCostCenterOptions([]); return; }
    setLoadingCostCenters(true);
    fetchAPI(`api/v2/CostCenter/GetAccountCostCenterForTimesheet/${activeEmpId}/${selectedProject.value}/${selectedTask.value}`, 'GET', '', true)
      .then((res) => res.json())
      .then((data) => {
        const formatted = (Array.isArray(data) ? data : data?.data || []).map((item: any) => ({ value: item.AccountCostCenterId, label: item.AccountCostCenter }));
        setCostCenterOptions(formatted);
        const targetId = activeTimerCostCenterId || savedCostCenterId;
        const match = formatted.find((c: any) => Number(c.value) === Number(targetId));
        setSelectedCostCenter(match || formatted[0] || null);
      })
      .catch((err) => console.error('Error fetching cost centers:', err))
      .finally(() => setLoadingCostCenters(false));
  }, [selectedTask?.value, selectedProject?.value, activeEmpId, isCostCenterVisible, savedCostCenterId, activeTimerCostCenterId]);

  useEffect(() => {
    let interval: any = null;
    if (timerState === 'running') {
      interval = setInterval(() => setSeconds((s) => s + 1), 1000);
    }
    return () => { if (interval) clearInterval(interval); };
  }, [timerState]);

  const handleStartTimer = async () => {
    const todayDate = moment().format('YYYY-MM-DD');
    const object = {
      AccountEmployeeTimeEntryPeriodId: accountEmployeeTimeEntryPeriodId || null,
      AccountClientId: isClientVisible ? (selectedClient?.value || 0) : 0,
      AccountProjectId: selectedProject?.value || 0,
      AccountProjectTaskId: selectedTask?.value || 0,
      TimerDate: todayDate,
      TimeEntryStartDate: periodDates.startDate || todayDate,
      TimeEntryEndDate: periodDates.endDate || todayDate,
      TimeEntryViewType: periodDates.viewType || "Weekly",
      AccountEmployeeTimeEntryId: null,
      AccountEmployeeId: Number(activeEmpId),
      AccountCostCenterId: isCostCenterVisible ? (selectedCostCenter?.value || 0) : 0,
      AccountWorkTypeId: isWorkTypeVisible ? (selectedWorkType?.value || defaultWorkTypeId || 0) : (defaultWorkTypeId || 0),
      Description: description,
      TimeZoneId: timeZoneId,
      SystemPlatformSourceId: "50E254BE-D515-4FBA-BFB7-DFD27852D023"
    };

    try {
      const response = await fetchAPI(`api/Timesheets/Timer?IsTimeEntryAutoSave=true`, 'POST', object, true);
      if (response.ok) {
        const json = await response.json();
        const newTimerId = json.id || json.TimeEntryTimerId;
        if (Number(newTimerId) > 0) { setLastEntryTimerId(newTimerId); setTimerState('running'); }
      }
    } catch (err) { console.error('Error starting timer API:', err); }
  };

  const handleStopTimer = () => {
    if (seconds < 60) { 
       ShowToast('Warning', 'To save this entry total time must be 1 minute or above.', 'warning', 'toast-top-right');
     
       return; 
    }
    setTimerState('stopped');
  };


  const handleSaveEntry = async () => {
  if (isSubmitting) return; 
  setIsSubmitting(true);
  
  try {
    const stopPayload = { 
      TimeEntryTimerStatus: 5, 
      AccountEmployeeTimeEntryPeriodId: accountEmployeeTimeEntryPeriodId || null, 
      Description: description, 
      SystemPlatformSourceId: "50E254BE-D515-4FBA-BFB7-DFD27852D023" 
    };
    
    const response = await fetchAPI(`api/Timesheets/ReactTimer/${lastEntryTimerId}?IsFromReact=true&totalSeconds=${seconds}`, 'POST', stopPayload, true);
    
    if (response.ok) {
      const result = await response.json();
      if (Number(result.id) > 0 || result.success) {
        ShowToast('Success', 'Time Entry Added Successfully!', 'success', 'toast-top-right');
        setTimerState('idle'); 
        setSeconds(0); 
        setDescription(''); 
        setLastEntryTimerId(null);
      } else { 
        ShowToast('Error', 'Failed to save time entry.', 'error', 'toast-top-right');
      }
    } else {
      ShowToast('Error', 'Failed to save time entry.', 'error', 'toast-top-right');
    }
  } catch (err) { 
    console.error('Error saving timer entry:', err); 
    ShowToast('Error', 'Something went wrong.', 'error', 'toast-top-right');
  } finally {
    setIsSubmitting(false); 
  }
};


  const formatTime = (totalSeconds: number) => {
    const hrs = Math.floor(totalSeconds / 3600);
    const mins = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;
    return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  if (timerState === 'stopped') {
    return (
      <div className="d-flex justify-content-center align-items-center w-100 py-3">
        <div className="bg-white rounded-4 p-4 border shadow-sm w-100" style={{ maxWidth: '360px', borderColor: '#E4E6EF' }}>
          <div className="text-center mb-3">
            <div className="d-inline-flex align-items-center justify-content-center rounded-circle mb-2" style={{ width: '48px', height: '48px', backgroundColor: '#48BB78' }}>
              <i className="bi bi-check-lg text-white fs-2 fw-bolder"></i>
            </div>
            <h5 className="fw-bolder text-dark m-0 fs-5" style={{ color: '#181C32' }}>Time Entry Complete</h5>
          </div>

          <div className="d-flex justify-content-between align-items-center mb-4 pt-2 px-1">
            <span className="fs-7 fw-semibold text-gray-500">Duration</span>
            <span className="fs-5 fw-bolder font-monospace" style={{ color: '#2e7d32' }}>{formatTime(seconds)}</span>
          </div>

          <div className="mb-4">
            {isClientVisible && (
              <div className="d-flex align-items-center justify-content-between mb-3">
                <div className="d-flex align-items-center text-gray-500 fs-7 fw-semibold"><i className="bi bi-building me-2 fs-6"></i><span>Client</span></div>
                <span className="fw-bold fs-7  text-truncate ms-2" style={{ maxWidth: '170px' }}>{selectedClient?.label || 'N/A'}</span>
              </div>
            )}
            <div className="d-flex align-items-center justify-content-between mb-3">
              <div className="d-flex align-items-center text-gray-500 fs-7 fw-semibold"><i className="bi bi-folder me-2 fs-6"></i><span>Project</span></div>
              <span className="fw-bold fs-7  text-truncate ms-2" style={{ maxWidth: '170px' }}>{selectedProject?.label || 'N/A'}</span>
            </div>
            <div className="d-flex align-items-center justify-content-between mb-3">
              <div className="d-flex align-items-center text-gray-500 fs-7 fw-semibold"><i className="bi bi-card-checklist me-2 fs-6"></i><span>Task</span></div>
              <span className="fw-bold fs-7  text-truncate ms-2" style={{ maxWidth: '170px' }}>{selectedTask?.label || 'N/A'}</span>
            </div>
            {isCostCenterVisible && (
              <div className="d-flex align-items-center justify-content-between mb-3">
                <div className="d-flex align-items-center text-gray-500 fs-7 fw-semibold"><i className="bi bi-diagram-3 me-2 fs-6"></i><span>Cost Center</span></div>
                <span className="fw-bold fs-7  text-truncate ms-2" style={{ maxWidth: '170px' }}>{selectedCostCenter?.label || 'N/A'}</span>
              </div>
            )}
            {isWorkTypeVisible && (
              <div className="d-flex align-items-center justify-content-between mb-3">
                <div className="d-flex align-items-center text-gray-500 fs-7 fw-semibold"><i className="bi bi-briefcase me-2 fs-6"></i><span>Work Type</span></div>
                <span className="fw-bold fs-7  text-truncate ms-2" style={{ maxWidth: '170px' }}>{selectedWorkType?.label || 'N/A'}</span>
              </div>
            )}
          </div>

          <div className="mb-4">
            <label className="form-label fs-7 fw-bold mb-2 text-dark">What did you work on?</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="form-control form-control-solid p-3 fs-7 border rounded-3"
              rows={3}
              style={{ resize: 'none', backgroundColor: '#fff', borderColor: '#E4E6EF' }}
              placeholder="Add a short description..."
            />
          </div>

          <div>
            <button 
  type="button" 
  onClick={handleSaveEntry} 
  disabled={isSubmitting}
  className="btn btn-primary btn-sm w-100 mb-2 fs-6 fw-bold py-2 shadow-sm d-flex align-items-center justify-content-center" 
  style={{ backgroundColor: '#1B64F2', borderColor: '#1B64F2', borderRadius: '10px' }}
>
  {isSubmitting ? (
    <>
      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
      Saving...
    </>
  ) : (
    'Save Entry'
  )}
</button>
            <button type="button" onClick={() => setTimerState('running')} className="btn btn-light border w-100 fs-6 fw-bold py-2 text-dark" style={{ borderRadius: '10px', backgroundColor: '#F9FAFB', borderColor: '#E5E7EB' }}>
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="d-flex flex-column h-100 justify-content-between">
      <div>
        {timerState === 'idle' && (
          <div className="d-flex justify-content-between align-items-center mb-3">
            
            <h6 className="fw-bolder text-dark fs-6 m-0" style={{ color: '#172b4d', letterSpacing: '-0.2px' }}>Select what you're working on</h6>
            <div className="d-flex align-items-center gap-1">
              <button type="button" onClick={handleRefreshAllPreferences} disabled={isRefreshing} className="btn btn-icon btn-active-light-primary btn-sm p-1 border-0 me-1" title="Sync & Reload Preferences" style={{ width: '28px', height: '28px' }}>
                <i className={`bi bi-arrow-repeat fs-6 text-primary ${isRefreshing ? 'spin-animation' : ''}`}></i>
              </button>
              {(selectedClient || selectedProject || selectedTask || selectedCostCenter || selectedWorkType || description) && (
                <button type="button" onClick={handleClearAll} className="btn btn-icon btn-active-light-danger btn-sm p-1 border-0" title="Clear all dropdowns and fields" style={{ width: '28px', height: '28px' }}>
                  <i className="bi bi-arrow-counterclockwise fs-7 text-danger"></i>
                </button>
              )}
            </div>
          </div>
        )}

        {timerState === 'running' && (
          <>
            <div className="d-flex align-items-center justify-content-center bg-light-success rounded-pill py-2 px-4 mb-3 mx-auto" style={{ backgroundColor: '#e8f5e9', maxWidth: '320px' }}>
              <span className="rounded-circle me-2" style={{ width: '10px', height: '10px', backgroundColor: '#2e7d32' }}></span>
              <span className="fs-6 fw-bold" style={{ color: '#2e7d32' }}>Timer in progress</span>
            </div>

            <div className="text-center my-4">
              <div className="fw-bold font-monospace" style={{ fontSize: '3.5rem', letterSpacing: '2px', lineHeight: '1.1', color: '#249129ee' }}>
                {formatTime(seconds)}
              </div>
              <div className="fs-6 fw-bold mt-2" style={{ color: '#5E6278' }}>Time in progress</div>
            </div>
          </>
        )}

        {isClientVisible && (
          <div className="mb-4">
            <label className="form-label fs-7 fw-bold mb-1" style={{ color: '#3F4254' }}>Client</label>
            <Select value={selectedClient} onChange={handleClientChange} options={clientOptions} isLoading={loadingClients} isDisabled={timerState === 'running'} styles={timerDropdownStyles} placeholder="Select Client" formatOptionLabel={(opt, { context }) => formatOptionWithIcon(opt, 'bi-building', context)} />
          </div>
        )}

        <div className="mb-4">
          <label className="form-label fs-7 fw-bold mb-1 extensionHeading" style={{ color: '#3F4254' }}>Project</label>
          <Select value={selectedProject} onChange={handleProjectChange} options={projectOptions} isLoading={loadingProjects} isDisabled={(isClientVisible && !selectedClient) || timerState === 'running'} styles={timerDropdownStyles} placeholder="Select Project" formatOptionLabel={(opt, { context }) => formatOptionWithIcon(opt, 'bi-folder', context)} />
        </div>

        <div className="mb-4">
          <label className="form-label fs-7 fw-bold mb-1 extensionHeading" style={{ color: '#3F4254' }}>Task</label>
          <Select value={selectedTask} onChange={handleTaskChange} options={taskOptions} isLoading={loadingTasks} isDisabled={!selectedProject || timerState === 'running'} styles={timerDropdownStyles} placeholder="Select Task" formatOptionLabel={(opt, { context }) => formatOptionWithIcon(opt, 'bi-card-checklist', context)} />
        </div>

        {isCostCenterVisible && (
          <div className="mb-4">
            <label className="form-label fs-7 fw-bold mb-1 extensionHeading"  style={{ color: '#3F4254' }}>Cost Center</label>
            <Select value={selectedCostCenter} onChange={handleCostCenterChange} options={costCenterOptions} isLoading={loadingCostCenters} isDisabled={!selectedTask || timerState === 'running'} styles={timerDropdownStyles} placeholder="Select Cost Center" formatOptionLabel={(opt, { context }) => formatOptionWithIcon(opt, 'bi-diagram-3', context)} />
          </div>
        )}

        {isWorkTypeVisible && (
          <div className="mb-4">
            <label className="form-label fs-7 fw-bold mb-1 extensionHeading" style={{ color: '#3F4254' }}>Work Type</label>
            <Select value={selectedWorkType} onChange={handleWorkTypeChange} options={workTypeOptions} isLoading={loadingWorkTypes} isDisabled={timerState === 'running'} styles={timerDropdownStyles} placeholder="Select Work Type" formatOptionLabel={(opt, { context }) => formatOptionWithIcon(opt, 'bi-briefcase', context)} />
          </div>
        )}
      </div>

      {timerState === 'idle' && (
        <div className="mt-4">
          <button type="button" disabled={!selectedTask} onClick={handleStartTimer} className="btn btn-extension-primary btn-sm w-100 mb-3 fs-6 fw-bold py-2 shadow-sm">
            <i className="bi bi-play-fill fs-5 me-1"></i> Start Timer
          </button>
          <div className="separator separator-content my-3 text-gray-500 fs-8 fw-semibold">or</div>
          <button type="button" onClick={onSwitchToManual} className="btn btn-extension-light-primary btn-sm w-100 fs-6 fw-bold py-2">
            <i className="bi bi-calendar-plus fs-7 me-1"></i> Enter Time Manually
          </button>
        </div>
      )}

      {timerState === 'running' && (
        <div className="mt-4">
          <button type="button" onClick={handleStopTimer} className="btn btn-outline btn-outline-danger btn-active-light-danger btn-sm w-100 fs-6 fw-bold py-2 d-flex align-items-center justify-content-center" style={{ borderRadius: '8px' }}>
            <span className="me-2" style={{ width: '12px', height: '12px', backgroundColor: '#dc3545', display: 'inline-block', borderRadius: '2px' }}></span> Stop
          </button>
        </div>
      )}
    </div>
  );
};

export default ExtensionTimer;

// import React, { useState, useEffect } from 'react';
// import Select from 'react-select';
// import fetchAPI from '../../common/fetchAPI';
// import { useAppSelector, useAppDispatch } from '../../app/hooks';
// import moment from 'moment';
// import { getGlobalAccountPreferenceDataAsync } from '../../common/GlobalSlice';
// import { getTimer } from '../timesheet/timesheetSlice';

// declare const chrome: any;

// interface ExtensionTimerProps {
//   onSwitchToManual: () => void;
// }

// const formatOptionWithIcon = (option: any, iconClass: string, context: 'menu' | 'value') => {
//   if (context === 'value') {
//     return (
//       <div className="d-flex align-items-center">
//         <i className={`bi ${iconClass} me-2 text-gray-700 fs-6`}></i>
//         <span className="fw-bold fs-7" style={{ color: '#181C32' }}>
//           {option.label}
//         </span>
//       </div>
//     );
//   }


//   return option.label;
// };

// const timerDropdownStyles = {
//   control: (base: any, state: any) => ({
//     ...base,
//     minHeight: '38px',
//     height: '38px',
//     borderRadius: '8px',
//     borderColor: state.isFocused ? '#1B64F2' : '#dddddd',
//     boxShadow: 'none',
//     backgroundColor: state.isDisabled ? '#F9FAFB' : '#FFFFFF',
//     '&:hover': {
//       borderColor: '#1B64F2',
//     },
//   }),
//   valueContainer: (base: any) => ({
//     ...base,
//     padding: '0 8px',
//     height: '38px',
//     display: 'flex',
//     alignItems: 'center',
//   }),
//   input: (base: any) => ({
//     ...base,
//     margin: 0,
//     padding: 0,
//     fontSize: '13px',
//   }),
//   singleValue: (base: any) => ({
//     ...base,
//     fontSize: '13px',
//     color: '#181C32', 
//     margin: 0,
//     display: 'flex',
//     alignItems: 'center',
//   }),
//   placeholder: (base: any) => ({
//     ...base,
//     fontSize: '13px',
//     color: '#A1A5B7',
//   }),
//   option: (base: any, state: any) => ({
//     ...base,
//     backgroundColor: state.isSelected 
//       ? '#1B64F2' 
//       : state.isFocused 
//         ? '#F4F6F9' 
//         : '#FFFFFF',
//     color: state.isSelected ? '#FFFFFF' : '#181C32',
//     fontSize: '13px',
//     cursor: 'pointer',
//     padding: '8px 12px',
//     fontWeight: state.isSelected ? '500' : '400',
//   }),
//   menu: (base: any) => ({
//     ...base,
//     borderRadius: '8px',
//     boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.08)',
//     zIndex: 9999,
//     overflow: 'hidden',
//   }),
//   menuList: (base: any) => ({
//     ...base,
//     padding: 0,
//   }),
// };
// export const ExtensionTimer: React.FC<ExtensionTimerProps> = ({ onSwitchToManual }) => {
//   const dispatch = useAppDispatch();

//   const { 
//     sessionAccountEmployeeId, 
//     timeZoneId, 
//     showClientInTimesheet, 
//     showCostCenterInTimeSheet, 
//     showWorkTypeInTimeSheet 
//   } = useAppSelector((state) => state.globalState);
  
//   const { defaultWorkTypeId } = useAppSelector((state) => state.timesheet);

//   const showClientFlag: boolean = Boolean(showClientInTimesheet);
//   const showCostCenterFlag: boolean = Boolean(showCostCenterInTimeSheet);
//   const showWorkTypeFlag: boolean = Boolean(showWorkTypeInTimeSheet);

//   const [activeEmpId, setActiveEmpId] = useState<number | string | null>(null);
//   const [isClientVisible, setIsClientVisible] = useState<boolean>(showClientFlag);
//   const [isCostCenterVisible, setIsCostCenterVisible] = useState<boolean>(showCostCenterFlag);
//   const [isWorkTypeVisible, setIsWorkTypeVisible] = useState<boolean>(showWorkTypeFlag);
//   const [isStorageLoaded, setIsStorageLoaded] = useState<boolean>(false);

//   const [clientOptions, setClientOptions] = useState<{ value: any; label: string }[]>([]);
//   const [projectOptions, setProjectOptions] = useState<{ value: any; label: string }[]>([]);
//   const [taskOptions, setTaskOptions] = useState<{ value: any; label: string }[]>([]);
//   const [costCenterOptions, setCostCenterOptions] = useState<{ value: any; label: string }[]>([]);
//   const [workTypeOptions, setWorkTypeOptions] = useState<{ value: any; label: string }[]>([]);

//   const [selectedClient, setSelectedClient] = useState<any>(null);
//   const [selectedProject, setSelectedProject] = useState<any>(null);
//   const [selectedTask, setSelectedTask] = useState<any>(null);
//   const [selectedCostCenter, setSelectedCostCenter] = useState<any>(null);
//   const [selectedWorkType, setSelectedWorkType] = useState<any>(null);

//   const [savedIds, setSavedIds] = useState<{
//     clientId?: any;
//     projectId?: any;
//     taskId?: any;
//     costCenterId?: any;
//     workTypeId?: any;
//   }>({});

//   const [activeTimerMeta, setActiveTimerMeta] = useState<{
//     clientId?: any;
//     projectId?: any;
//     taskId?: any;
//     costCenterId?: any;
//     workTypeId?: any;
//   }>({});

//   const [loadingClients, setLoadingClients] = useState(false);
//   const [loadingProjects, setLoadingProjects] = useState(false);
//   const [loadingTasks, setLoadingTasks] = useState(false);
//   const [loadingCostCenters, setLoadingCostCenters] = useState(false);
//   const [loadingWorkTypes, setLoadingWorkTypes] = useState(false);

//   const [timerState, setTimerState] = useState<'idle' | 'running' | 'stopped'>('idle');
//   const [seconds, setSeconds] = useState(0);
//   const [description, setDescription] = useState('');
//   const [lastEntryTimerId, setLastEntryTimerId] = useState<any>(null);
//   const [accountEmployeeTimeEntryPeriodId, setAccountEmployeeTimeEntryPeriodId] = useState<any>(null);
//   const [periodDates, setPeriodDates] = useState({ startDate: '', endDate: '', viewType: 'Weekly' });

//   const persistStorage = (dataToSave: Record<string, any>) => {
//     if (typeof chrome !== 'undefined' && chrome.storage?.local) {
//       chrome.storage.local.set(dataToSave);
//     } else {
//       Object.entries(dataToSave).forEach(([k, v]) => {
//         if (v === null || v === undefined) localStorage.removeItem(k);
//         else localStorage.setItem(k, JSON.stringify(v));
//       });
//     }
//   };

//   useEffect(() => {
//     setIsClientVisible(showClientFlag);
//     setIsCostCenterVisible(showCostCenterFlag);
//     setIsWorkTypeVisible(showWorkTypeFlag);
//   }, [showClientFlag, showCostCenterFlag, showWorkTypeFlag]);

//   useEffect(() => {
//     const keysToFetch = [
//       'sessionAccountEmployeeId',
//       'accountEmployeeId',
//       'showClientInTimesheet',
//       'showCostCenterInTimeSheet',
//       'showWorkTypeInTimeSheet',
//       'timeZoneId',
//       'manual_clientId',
//       'manual_projectId',
//       'manual_taskId',
//       'manual_costCenterId',
//       'manual_workTypeId'
//     ];

//     if (typeof chrome !== 'undefined' && chrome.storage?.local) {
//       chrome.storage.local.get(keysToFetch, (result: any) => {
//         const resolvedEmpId = result?.accountEmployeeId || result?.sessionAccountEmployeeId || sessionAccountEmployeeId;
//         if (resolvedEmpId) setActiveEmpId(Number(resolvedEmpId));

//         if (result?.showClientInTimesheet !== undefined) setIsClientVisible(Boolean(result.showClientInTimesheet));
//         if (result?.showCostCenterInTimeSheet !== undefined) setIsCostCenterVisible(Boolean(result.showCostCenterInTimeSheet));
//         if (result?.showWorkTypeInTimeSheet !== undefined) setIsWorkTypeVisible(Boolean(result.showWorkTypeInTimeSheet));

//         setSavedIds({
//           clientId: result?.manual_clientId ?? null,
//           projectId: result?.manual_projectId ?? null,
//           taskId: result?.manual_taskId ?? null,
//           costCenterId: result?.manual_costCenterId ?? null,
//           workTypeId: result?.manual_workTypeId ?? null,
//         });

//         setIsStorageLoaded(true);
//         dispatch(getGlobalAccountPreferenceDataAsync() as any);
//       });
//     } else {
//       if (sessionAccountEmployeeId) setActiveEmpId(Number(sessionAccountEmployeeId));
//       const parseLocal = (k: string) => {
//         const val = localStorage.getItem(k);
//         return val ? JSON.parse(val) : null;
//       };
//       setSavedIds({
//         clientId: parseLocal('manual_clientId'),
//         projectId: parseLocal('manual_projectId'),
//         taskId: parseLocal('manual_taskId'),
//         costCenterId: parseLocal('manual_costCenterId'),
//         workTypeId: parseLocal('manual_workTypeId'),
//       });
//       setIsStorageLoaded(true);
//       dispatch(getGlobalAccountPreferenceDataAsync() as any);
//     }
//   }, [sessionAccountEmployeeId, dispatch]);

//   const handleClearAll = () => {
    
//     setSelectedClient(null);
//     setSelectedProject(null);
//     setSelectedTask(null);
//     setSelectedCostCenter(null);
//     setSelectedWorkType(null);
//     setDescription('');

//     if (isClientVisible) {
//       setProjectOptions([]);
//     }
//     setTaskOptions([]);
//     setCostCenterOptions([]);

//     setSavedIds({
//       clientId: null,
//       projectId: null,
//       taskId: null,
//       costCenterId: null,
//       workTypeId: null,
//     });

//     persistStorage({
//       manual_clientId: null,
//       manual_projectId: null,
//       manual_taskId: null,
//       manual_costCenterId: null,
//       manual_workTypeId: null,
//     });
//   };

//   const handleClientChange = (opt: any) => {
//     setSelectedClient(opt);
//     const val = opt?.value ?? null;
//     setSavedIds((prev) => ({ ...prev, clientId: val, projectId: null, taskId: null, costCenterId: null }));
    
//     persistStorage({
//       manual_clientId: val,
//       manual_projectId: null,
//       manual_taskId: null,
//       manual_costCenterId: null,
//     });

//     if (!opt) {
//       setSelectedProject(null);
//       setSelectedTask(null);
//       setSelectedCostCenter(null);
//     }
//   };

//   const handleProjectChange = (opt: any) => {
//     setSelectedProject(opt);
//     const val = opt?.value ?? null;
//     setSavedIds((prev) => ({ ...prev, projectId: val, taskId: null, costCenterId: null }));
    
//     persistStorage({
//       manual_projectId: val,
//       manual_taskId: null,
//       manual_costCenterId: null,
//     });

//     if (!opt) {
//       setSelectedTask(null);
//       setSelectedCostCenter(null);
//     }
//   };

//   const handleTaskChange = (opt: any) => {
//     setSelectedTask(opt);
//     const val = opt?.value ?? null;
//     setSavedIds((prev) => ({ ...prev, taskId: val, costCenterId: null }));
    
//     persistStorage({
//       manual_taskId: val,
//       manual_costCenterId: null,
//     });

//     if (!opt) {
//       setSelectedCostCenter(null);
//     }
//   };

//   const handleCostCenterChange = (opt: any) => {
//     setSelectedCostCenter(opt);
//     const val = opt?.value ?? null;
//     setSavedIds((prev) => ({ ...prev, costCenterId: val }));
//     persistStorage({ manual_costCenterId: val });
//   };

//   const handleWorkTypeChange = (opt: any) => {
//     setSelectedWorkType(opt);
//     const val = opt?.value ?? null;
//     setSavedIds((prev) => ({ ...prev, workTypeId: val }));
//     persistStorage({ manual_workTypeId: val });
//   };

//   useEffect(() => {
//     if (!isStorageLoaded || !activeEmpId) return;

//     const todayDate = moment().format('YYYY-MM-DD');

//     fetchAPI(`api/Timesheets/GetTimesheetPeriodForReact/${activeEmpId}/${todayDate}`, 'GET', '', true)
//       .then((res) => res.json())
//       .then((data) => {
//         const targetData = data?.data || data;
//         const periodObj = targetData?.AccountEmployeeTimeEntryPeriod || {};
//         if (periodObj?.AccountEmployeeTimeEntryPeriodId) {
//           setAccountEmployeeTimeEntryPeriodId(periodObj.AccountEmployeeTimeEntryPeriodId);
//           setPeriodDates({
//             startDate: periodObj?.TimeEntryStartDate || todayDate,
//             endDate: periodObj?.TimeEntryEndDate || todayDate,
//             viewType: periodObj?.TimeEntryViewType || 'Weekly'
//           });
//         }
//       })
//       .catch((err) => console.error('Error fetching period:', err));

//     dispatch(getTimer() as any).then((action: any) => {
//       const payload = action?.payload;
//       const timerList = Array.isArray(payload) ? payload : (payload?.data || []);
      
//       if (timerList.length > 0) {
//         const activeTimer = timerList[0];
//         setLastEntryTimerId(activeTimer.TimeEntryTimerId);
//         setSeconds(activeTimer.TotalSeconds || 0);
//         setDescription(activeTimer.Description || '');
//         setTimerState('running');

//         setActiveTimerMeta({
//           clientId: activeTimer.AccountClientId || null,
//           projectId: activeTimer.AccountProjectId || null,
//           taskId: activeTimer.AccountProjectTaskId || null,
//           workTypeId: activeTimer.AccountWorkTypeId || null,
//           costCenterId: activeTimer.AccountCostCenterId || null
//         });
//       }
//     });
//   }, [isStorageLoaded, activeEmpId, dispatch]);

//   useEffect(() => {
//     if (!isStorageLoaded || !activeEmpId) return;

//     if (isClientVisible) {
//       setLoadingClients(true);
//       fetchAPI(`api/Timesheets/GetAccountClientForTimesheet/${activeEmpId}/0`, 'GET', '', true)
//         .then((res) => res.json())
//         .then((data) => {
//           const list = Array.isArray(data) ? data : data?.data || [];
//           const formatted = list.map((item: any) => ({
//             value: item.AccountClientId,
//             label: item.ClientName,
//           }));
//           setClientOptions(formatted);

//           const targetId = savedIds.clientId;
//           if (targetId) {
//             const match = formatted.find((c: any) => Number(c.value) === Number(targetId));
//             if (match) setSelectedClient(match);
//           } else {
//             setSelectedClient(null);
//           }
//         })
//         .catch((err) => console.error('Error fetching clients:', err))
//         .finally(() => setLoadingClients(false));
//     } else {
//       setLoadingProjects(true);
//       fetchAPI(`api/Projects/GetAccountProjectsForTimesheet/0/${activeEmpId}`, 'GET', '', true)
//         .then((res) => res.json())
//         .then((data) => {
//           const list = Array.isArray(data) ? data : data?.data || [];
//           const formatted = list.map((item: any) => ({
//             value: item.AccountProjectId,
//             label: item.ProjectName,
//           }));
//           setProjectOptions(formatted);

//           const targetId = savedIds.projectId;
//           if (targetId) {
//             const match = formatted.find((p: any) => Number(p.value) === Number(targetId));
//             if (match) setSelectedProject(match);
//           } else {
//             setSelectedProject(null);
//           }
//         })
//         .catch((err) => console.error('Error fetching projects:', err))
//         .finally(() => setLoadingProjects(false));
//     }

//     if (isWorkTypeVisible) {
//       setLoadingWorkTypes(true);
//       fetchAPI('api/WorkTypes', 'GET', '', true)
//         .then((res) => res.json())
//         .then((data) => {
//           const list = Array.isArray(data) ? data : data?.data || [];
//           const formatted = list.map((item: any) => ({
//             value: item.AccountWorkTypeId,
//             label: item.AccountWorkType,
//           }));
//           setWorkTypeOptions(formatted);

//           const targetId = savedIds.workTypeId;
//           if (targetId) {
//             const match = formatted.find((w: any) => Number(w.value) === Number(targetId));
//             if (match) setSelectedWorkType(match);
//           } else if (!savedIds.hasOwnProperty('workTypeId') && defaultWorkTypeId) {
//             const match = formatted.find((w: any) => Number(w.value) === Number(defaultWorkTypeId));
//             if (match) setSelectedWorkType(match);
//           } else {
//             setSelectedWorkType(null);
//           }
//         })
//         .catch((err) => console.error('Error fetching work types:', err))
//         .finally(() => setLoadingWorkTypes(false));
//     }
//   }, [isStorageLoaded, activeEmpId, isClientVisible, isWorkTypeVisible, savedIds.clientId, savedIds.projectId, savedIds.workTypeId]);

//   useEffect(() => {
//     if (!isClientVisible || !selectedClient?.value || !activeEmpId) {
//       if (isClientVisible && !selectedClient) {
//         setProjectOptions([]);
//         setSelectedProject(null);
//       }
//       return;
//     }

//     setLoadingProjects(true);
//     fetchAPI(`api/Projects/GetAccountProjectsForTimesheet/${selectedClient.value}/${activeEmpId}`, 'GET', '', true)
//       .then((res) => res.json())
//       .then((data) => {
//         const list = Array.isArray(data) ? data : data?.data || [];
//         const formatted = list.map((item: any) => ({
//           value: item.AccountProjectId,
//           label: item.ProjectName,
//         }));
//         setProjectOptions(formatted);

//         const targetId = savedIds.projectId;
//         if (targetId) {
//           const match = formatted.find((p: any) => Number(p.value) === Number(targetId));
//           if (match) setSelectedProject(match);
//         } else {
//           setSelectedProject(null);
//         }
//       })
//       .catch((err) => console.error('Error fetching projects:', err))
//       .finally(() => setLoadingProjects(false));
//   }, [selectedClient, activeEmpId, isClientVisible, savedIds.projectId]);

//   useEffect(() => {
//     if (!selectedProject?.value || !activeEmpId) {
//       setTaskOptions([]);
//       return;
//     }

//     setLoadingTasks(true);
//     fetchAPI(`api/Timesheets/GetAccountProjectTasksForTimesheet/${selectedProject.value}/${activeEmpId}`, 'GET', '', true)
//       .then((res) => res.json())
//       .then((data) => {
//         const list = Array.isArray(data) ? data : data?.data || [];
//         const formatted = list.map((item: any) => ({
//           value: item.AccountProjectTaskId,
//           label: item.TaskName,
//         }));
//         setTaskOptions(formatted);

//         const targetId = activeTimerMeta.taskId || savedIds.taskId;
//         if (targetId) {
//           const match = formatted.find((t: any) => t.value === targetId);
//           if (match) setSelectedTask(match);
//         }
//       })
//       .catch((err) => console.error('Error fetching tasks:', err))
//       .finally(() => setLoadingTasks(false));
//   }, [selectedProject?.value, activeEmpId, savedIds.taskId, activeTimerMeta.taskId]);

//   useEffect(() => {
//     if (!isCostCenterVisible || !selectedTask?.value || !selectedProject?.value || !activeEmpId) {
//       setCostCenterOptions([]);
//       return;
//     }

//     setLoadingCostCenters(true);
//     fetchAPI(`api/v2/CostCenter/GetAccountCostCenterForTimesheet/${activeEmpId}/${selectedProject.value}/${selectedTask.value}`, 'GET', '', true)
//       .then((res) => res.json())
//       .then((data) => {
//         const list = Array.isArray(data) ? data : data?.data || [];
//         const formatted = list.map((item: any) => ({
//           value: item.AccountCostCenterId,
//           label: item.AccountCostCenter,
//         }));
//         setCostCenterOptions(formatted);

//         const targetId = activeTimerMeta.costCenterId || savedIds.costCenterId;
//         if (targetId) {
//           const match = formatted.find((c: any) => c.value === targetId);
//           if (match) setSelectedCostCenter(match);
//         } else if (formatted.length > 0) {
//           setSelectedCostCenter(formatted[0]);
//         }
//       })
//       .catch((err) => console.error('Error fetching cost centers:', err))
//       .finally(() => setLoadingCostCenters(false));
//   }, [selectedTask?.value, selectedProject?.value, activeEmpId, isCostCenterVisible, savedIds.costCenterId, activeTimerMeta.costCenterId]);

//   useEffect(() => {
//     let interval: any = null;
//     if (timerState === 'running') {
//       interval = setInterval(() => setSeconds((s) => s + 1), 1000);
//     }
//     return () => clearInterval(interval);
//   }, [timerState]);

//   const formatTime = (totalSeconds: number) => {
//     const hrs = Math.floor(totalSeconds / 3600);
//     const mins = Math.floor((totalSeconds % 3600) / 60);
//     const secs = totalSeconds % 60;
//     return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
//   };

//   const handleStartTimer = async () => {
//     const todayDate = moment().format('YYYY-MM-DD');
//     const object = {
//       AccountEmployeeTimeEntryPeriodId: accountEmployeeTimeEntryPeriodId || null,
//       AccountClientId: isClientVisible ? (selectedClient?.value || 0) : 0,
//       AccountProjectId: selectedProject?.value || 0,
//       AccountProjectTaskId: selectedTask?.value || 0,
//       TimerDate: todayDate,
//       TimeEntryStartDate: periodDates.startDate || todayDate,
//       TimeEntryEndDate: periodDates.endDate || todayDate,
//       TimeEntryViewType: periodDates.viewType || "Weekly",
//       AccountEmployeeTimeEntryId: null,
//       AccountEmployeeId: Number(activeEmpId),
//       AccountCostCenterId: isCostCenterVisible ? (selectedCostCenter?.value || 0) : 0,
//       AccountWorkTypeId: isWorkTypeVisible ? (selectedWorkType?.value || defaultWorkTypeId || 0) : (defaultWorkTypeId || 0),
//       Description: description,
//       TimeZoneId: timeZoneId,
//       SystemPlatformSourceId: "50E254BE-D515-4FBA-BFB7-DFD27852D023"
//     };

//     try {
//       const response = await fetchAPI(`api/Timesheets/Timer?IsTimeEntryAutoSave=true`, 'POST', object, true);
//       if (response.ok) {
//         const json = await response.json();
//         const newTimerId = json.id || json.TimeEntryTimerId;
//         if (Number(newTimerId) > 0) {
//           setLastEntryTimerId(newTimerId);
//           setTimerState('running');
//         }
//       }
//     } catch (err) {
//       console.error('Error starting timer API:', err);
//     }
//   };

//   const handleStopTimer = () => {
//     if (seconds < 60) {
//       alert("To save this entry total time must be 1 minute or above.");
//       return;
//     }
//     setTimerState('stopped');
//   };

//   const handleSaveEntry = async () => {
//     try {
//       const stopPayload = {
//         TimeEntryTimerStatus: 5,
//         AccountEmployeeTimeEntryPeriodId: accountEmployeeTimeEntryPeriodId || null,
//         Description: description,
//         SystemPlatformSourceId: "50E254BE-D515-4FBA-BFB7-DFD27852D023"
//       };

//       const response = await fetchAPI(
//         `api/Timesheets/ReactTimer/${lastEntryTimerId}?IsFromReact=true&totalSeconds=${seconds}`, 
//         'POST', 
//         stopPayload, 
//         true
//       );

//       if (response.ok) {
//         const result = await response.json();
//         if (Number(result.id) > 0 || result.success) {
//           alert("Time Entry Saved Successfully!");
//           setTimerState('idle');
//           setSeconds(0);
//           setDescription('');
//           setLastEntryTimerId(null);
//         } else {
//           alert("Failed to save entry.");
//         }
//       }
//     } catch (err) {
//       console.error('Error saving timer entry:', err);
//     }
//   };

//   if (timerState === 'stopped') {
//     return (
//       <div className="d-flex justify-content-center align-items-center w-100 py-3">
//         <div 
//           className="bg-white rounded-4 p-4 border shadow-sm w-100" 
//           style={{ maxWidth: '360px', borderColor: '#E4E6EF' }}
//         >
//           <div className="text-center mb-3">
//             <div 
//               className="d-inline-flex align-items-center justify-content-center rounded-circle mb-2" 
//               style={{ width: '48px', height: '48px', backgroundColor: '#48BB78' }}
//             >
//               <i className="bi bi-check-lg text-white fs-2 fw-bolder"></i>
//             </div>
//             <h5 className="fw-bolder text-dark m-0 fs-5" style={{ color: '#181C32' }}>
//               Time Entry Complete
//             </h5>
//           </div>

//           <div className="d-flex justify-content-between align-items-center mb-4 pt-2 px-1">
//             <span className="fs-7 fw-semibold text-gray-500">Duration</span>
//             <span className="fs-5 fw-bolder font-monospace" style={{ color: '#2e7d32' }}>
//               {formatTime(seconds)}
//             </span>
//           </div>

//           <div className="mb-4">
//             {isClientVisible && (
//               <div className="d-flex align-items-center justify-content-between mb-3">
//                 <div className="d-flex align-items-center text-gray-500 fs-7 fw-semibold">
//                   <i className="bi bi-building me-2 fs-6 text-gray-500"></i>
//                   <span>Client</span>
//                 </div>
//                 <span className="fw-bold fs-7 text-dark text-truncate ms-2" style={{ maxWidth: '170px' }}>
//                   {selectedClient?.label || 'N/A'}
//                 </span>
//               </div>
//             )}

//             <div className="d-flex align-items-center justify-content-between mb-3">
//               <div className="d-flex align-items-center text-gray-500 fs-7 fw-semibold">
//                 <i className="bi bi-folder me-2 fs-6 text-gray-500"></i>
//                 <span>Project</span>
//               </div>
//               <span className="fw-bold fs-7 text-dark text-truncate ms-2" style={{ maxWidth: '170px' }}>
//                 {selectedProject?.label || 'N/A'}
//               </span>
//             </div>

//             <div className="d-flex align-items-center justify-content-between mb-3">
//               <div className="d-flex align-items-center text-gray-500 fs-7 fw-semibold">
//                 <i className="bi bi-card-checklist me-2 fs-6 text-gray-500"></i>
//                 <span>Task</span>
//               </div>
//               <span className="fw-bold fs-7 text-dark text-truncate ms-2" style={{ maxWidth: '170px' }}>
//                 {selectedTask?.label || 'N/A'}
//               </span>
//             </div>

//             {isCostCenterVisible && (
//               <div className="d-flex align-items-center justify-content-between mb-3">
//                 <div className="d-flex align-items-center text-gray-500 fs-7 fw-semibold">
//                   <i className="bi bi-diagram-3 me-2 fs-6 text-gray-500"></i>
//                   <span>Cost Center</span>
//                 </div>
//                 <span className="fw-bold fs-7 text-dark text-truncate ms-2" style={{ maxWidth: '170px' }}>
//                   {selectedCostCenter?.label || 'N/A'}
//                 </span>
//               </div>
//             )}

//             {isWorkTypeVisible && (
//               <div className="d-flex align-items-center justify-content-between mb-3">
//                 <div className="d-flex align-items-center text-gray-500 fs-7 fw-semibold">
//                   <i className="bi bi-briefcase me-2 fs-6 text-gray-500"></i>
//                   <span>Work Type</span>
//                 </div>
//                 <span className="fw-bold fs-7 text-dark text-truncate ms-2" style={{ maxWidth: '170px' }}>
//                   {selectedWorkType?.label || 'N/A'}
//                 </span>
//               </div>
//             )}
//           </div>

//           <div className="mb-4">
//             <label className="form-label fs-7 fw-bold mb-2 text-dark">
//               What did you work on?
//             </label>
//             <textarea
//               value={description}
//               onChange={(e) => setDescription(e.target.value)}
//               className="form-control form-control-solid p-3 fs-7 border rounded-3"
//               rows={3}
//               style={{ resize: 'none', backgroundColor: '#fff', borderColor: '#E4E6EF' }}
//               placeholder="Add a short description..."
//             />
//           </div>

//           <div>
//             <button 
//               type="button"
//               onClick={handleSaveEntry} 
//               className="btn btn-primary btn-sm w-100 mb-2 fs-6 fw-bold py-2 shadow-sm"
//               style={{ backgroundColor: '#1B64F2', borderColor: '#1B64F2', borderRadius: '10px' }}
//             >
//               Save Entry
//             </button>
//             <button
//               type="button" 
//               onClick={() => setTimerState('running')} 
//               className="btn btn-light border w-100 fs-6 fw-bold py-2 text-dark"
//               style={{ borderRadius: '10px', backgroundColor: '#F9FAFB', borderColor: '#E5E7EB' }}
//             >
//               Cancel
//             </button>
//           </div>
//         </div>
//       </div>
//     );
//   }

//   return (
//     <div className="d-flex flex-column h-100 justify-content-between">
//       <div>
//         {timerState === 'idle' && (
//           <div className="d-flex justify-content-between align-items-center mb-3">
//             <h6 className="fw-bold fs-6 m-0" style={{ color: '#181C32', letterSpacing: '-0.2px' }}>
//               Select what you're working on
//             </h6>
//             {(selectedClient || selectedProject || selectedTask || selectedCostCenter || selectedWorkType || description) && (
//               <button
//                 type="button"
//                 onClick={handleClearAll}
//                 className="btn btn-icon btn-active-light-danger btn-sm p-1 border-0"
//                 title="Clear all dropdowns and fields"
//                 style={{ width: '28px', height: '28px' }}
//               >
//                 <i className="bi bi-arrow-counterclockwise fs-7 text-danger"></i>
//               </button>
//             )}
//           </div>
//         )}

//         {timerState === 'running' && (
//           <>
//             <div 
//               className="d-flex align-items-center justify-content-center bg-light-success rounded-pill py-2 px-4 mb-3 mx-auto" 
//               style={{ backgroundColor: '#e8f5e9', maxWidth: '320px' }}
//             >
//               <span 
//                 className="rounded-circle me-2" 
//                 style={{ width: '10px', height: '10px', backgroundColor: '#2e7d32' }}
//               ></span>
//               <span className="fs-6 fw-bold" style={{ color: '#2e7d32', WebkitFontSmoothing: 'antialiased' }}>
//                 Timer in progress
//               </span>
//             </div>

//             <div className="text-center my-4">
//               <div 
//                 className="fw-bold font-monospace" 
//                 style={{ 
//                   fontSize: '3.5rem', 
//                   letterSpacing: '2px', 
//                   lineHeight: '1.1',
//                   color: '#249129ee',
//                   WebkitFontSmoothing: 'antialiased',
//                   textShadow: '0 0 1px rgba(8, 180, 49, 0.1)'
//                 }}
//               >
//                 {formatTime(seconds)}
//               </div>
//               <div className="fs-6 fw-bold mt-2" style={{ color: '#5E6278' }}>
//                 Time in progress
//               </div>
//             </div>
//           </>
//         )}

//         {isClientVisible && (
//           <div className="mb-3">
//             <label className="form-label fs-7 fw-bold mb-1" style={{ color: '#3F4254' }}>
//               Client
//             </label>
//             <Select
//               value={selectedClient}
//               onChange={handleClientChange}
//               options={clientOptions}
//               isLoading={loadingClients}
//               isDisabled={timerState === 'running'}
//               styles={timerDropdownStyles}
//               placeholder="Select Client"
//               formatOptionLabel={(opt, { context }) => formatOptionWithIcon(opt, 'bi-building', context)}
//             />
//           </div>
//         )}

//         <div className="mb-3">
//           <label className="form-label fs-7 fw-bold mb-1" style={{ color: '#3F4254' }}>
//             Project
//           </label>
//           <Select
//             value={selectedProject}
//             onChange={handleProjectChange}
//             options={projectOptions}
//             isLoading={loadingProjects}
//             isDisabled={(isClientVisible && !selectedClient) || timerState === 'running'}
//             styles={timerDropdownStyles}
//             placeholder="Select Project"
//             formatOptionLabel={(opt, { context }) => formatOptionWithIcon(opt, 'bi-folder', context)}
//           />
//         </div>

//         <div className="mb-3">
//           <label className="form-label fs-7 fw-bold mb-1" style={{ color: '#3F4254' }}>
//             Task
//           </label>
//           <Select
//             value={selectedTask}
//             onChange={handleTaskChange}
//             options={taskOptions}
//             isLoading={loadingTasks}
//             isDisabled={!selectedProject || timerState === 'running'}
//             styles={timerDropdownStyles}
//             placeholder="Select Task"
//             formatOptionLabel={(opt, { context }) => formatOptionWithIcon(opt, 'bi-card-checklist', context)}
//           />
//         </div>

//         {isCostCenterVisible && (
//           <div className="mb-3">
//             <label className="form-label fs-7 fw-bold mb-1" style={{ color: '#3F4254' }}>
//               Cost Center
//             </label>
//             <Select
//               value={selectedCostCenter}
//               onChange={handleCostCenterChange}
//               options={costCenterOptions}
//               isLoading={loadingCostCenters}
//               isDisabled={!selectedTask || timerState === 'running'}
//               styles={timerDropdownStyles}
//               placeholder="Select Cost Center"
//               formatOptionLabel={(opt, { context }) => formatOptionWithIcon(opt, 'bi-diagram-3', context)}
//             />
//           </div>
//         )}

//         {isWorkTypeVisible && (
//           <div className="mb-3">
//             <label className="form-label fs-7 fw-bold mb-1" style={{ color: '#3F4254' }}>
//               Work Type
//             </label>
//             <Select
//               value={selectedWorkType}
//               onChange={handleWorkTypeChange}
//               options={workTypeOptions}
//               isLoading={loadingWorkTypes}
//               isDisabled={timerState === 'running'}
//               styles={timerDropdownStyles}
//               placeholder="Select Work Type"
//               formatOptionLabel={(opt, { context }) => formatOptionWithIcon(opt, 'bi-briefcase', context)}
//             />
//           </div>
//         )}
//       </div>

//       {timerState === 'idle' && (
//         <div className="mt-4">
//           <button
//             type="button"
//             disabled={!selectedTask}
//             onClick={handleStartTimer}
//             className="btn btn-extension-primary btn-sm w-100 mb-3 fs-6 fw-bold py-2 shadow-sm"
//           >
//             <i className="bi bi-play-fill fs-5 me-1"></i> Start Timer
//           </button>
//           <div className="separator separator-content my-3 text-gray-500 fs-8 fw-semibold">or</div>
//           <button
//             type="button"
//             onClick={onSwitchToManual}
//             className="btn btn-extension-light-primary btn-sm w-100 fs-6 fw-bold py-2"
//           >
//             <i className="bi bi-calendar-plus fs-7 me-1"></i> Enter Time Manually
//           </button>
//         </div>
//       )}

//       {timerState === 'running' && (
//         <div className="mt-4">
//           <button
//             type="button"
//             onClick={handleStopTimer}
//             className="btn btn-outline btn-outline-danger btn-active-light-danger btn-sm w-100 fs-6 fw-bold py-2 d-flex align-items-center justify-content-center"
//             style={{ borderRadius: '8px' }}
//           >
//             <span className="me-2" style={{ width: '12px', height: '12px', backgroundColor: '#dc3545', display: 'inline-block', borderRadius: '2px' }}></span> Stop
//           </button>
//         </div>
//       )}
//     </div>
//   );
// };

// export default ExtensionTimer;
